import { useCallback } from 'react';
import {
  DropzoneOptions,
  FileRejection,
  FileWithPath,
  useDropzone
} from 'react-dropzone';

import type { FileSpec } from 'client-types/';
import {
  validateMultipleFiles,
  getFormattedErrorMessage,
  DEFAULT_MAX_FILE_SIZE_MB,
  DEFAULT_MAX_FILES_COUNT,
  DEFAULT_ALLOWED_FILE_EXTENSIONS
} from '@/utils/fileValidation';

interface useUploadProps {
  onError?: (error: string) => void;
  onResolved: (payloads: FileWithPath[]) => void;
  options?: DropzoneOptions;
  spec: FileSpec;
  maxSizeMB?: number;
  maxFiles?: number;
  allowedExtensions?: string[];
}

const useUpload = ({ onError, onResolved, options, spec }: useUploadProps) => {
  const describeAllowedExtensions = (): string => {
    const accept = spec.accept;
    let extensions: string[] = [];

    if (Array.isArray(accept)) {
      extensions = accept
        .filter((entry): entry is string => typeof entry === 'string')
        .map((entry) => (entry.startsWith('.') ? entry : entry.replace('*', '')));
    } else if (accept && typeof accept === 'object') {
      extensions = Object.values(accept)
        .flat()
        .filter(Boolean)
        .map((entry) => (entry.startsWith('.') ? entry : `.${entry}`));
    }

    return extensions
      .map((ext) => ext.toUpperCase())
      .join(', ');
  };

  const allowedExtensionsLabel = describeAllowedExtensions();

  const onDrop: DropzoneOptions['onDrop'] = useCallback(
    (acceptedFiles: FileWithPath[], fileRejections: FileRejection[]) => {
      // Check for dropzone-level rejections first
      if (fileRejections.length > 0) {
        const error = fileRejections[0].errors[0];
        console.error('[useUpload::onDrop] File rejection', {
          errorCode: error.code,
          errorMessage: error.message,
          specMaxSize: spec.max_size_mb,
          specMaxFiles: spec.max_files
        });

        if (error.code === 'file-too-large') {
          onError?.(`File is larger than ${spec.max_size_mb} MB`);
        } else if (error.code === 'file-invalid-type') {
          onError?.(
            allowedExtensionsLabel
              ? `Only ${allowedExtensionsLabel} files are allowed.`
              : 'Selected file type is not allowed.'
          );
        } else if (error.code === 'too-many-files') {
          onError?.(`Maximum ${spec.max_files || 1} files allowed.`);
        } else {
          onError?.(error.message);
        }
      }

      if (!acceptedFiles.length) {
        return;
      }

      // Perform comprehensive validation on accepted files
      const validationResult = validateMultipleFiles(
        acceptedFiles as File[],
        DEFAULT_MAX_FILES_COUNT,
        DEFAULT_MAX_FILE_SIZE_MB
      );

      if (!validationResult.valid) {
        const errorMessage = getFormattedErrorMessage(validationResult);
        console.error('[useUpload::onDrop] Validation failed', {
          errorMessage,
          errors: validationResult.errors
        });
        onError?.(errorMessage);
        return;
      }

      return onResolved(acceptedFiles);
    },
    [spec, onError, onResolved, allowedExtensionsLabel]
  );

  let dzAccept: Record<string, string[]> = {};
  const accept = spec.accept;

  if (Array.isArray(accept)) {
    accept.forEach((a) => {
      if (typeof a === 'string') {
        dzAccept[a] = [];
      }
    });
  } else if (typeof accept === 'object') {
    dzAccept = accept;
  }

  console.log('[useUpload] Dropzone accept config', { dzAccept });

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: spec.max_files || undefined,
    accept: dzAccept,
    maxSize: (spec.max_size_mb || 2) * 1000000,
    ...options
  });

  return { getInputProps, getRootProps, isDragActive };
};

export { useUpload };
